import { ref, onMounted, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'

export const useSound = () => {
  const { isSoundEnabled } = storeToRefs(useAppStore())
  const ambientAudio = ref<HTMLAudioElement | null>(null)
  const countdownAudio = ref<HTMLAudioElement | null>(null)
  const preloadedAudios = ref<Record<string, HTMLAudioElement>>({})

  // Preload all sound files
  const preloadSounds = () => {
    const soundFiles = [
      '/audio/button-click.mp3',
      '/audio/card-traded.wav',
      '/audio/ambient.mp3',
      '/audio/game-won.mp3',
      '/audio/trash-card.mp3',
      '/audio/correct-answer.mp3',
      '/audio/wrong-answer.mp3',
      '/audio/countdown.mp3',
      '/audio/word-formed.mp3'
    ]

    soundFiles.forEach(src => {
      const audioUrl = new URL(src, import.meta.url).href
      const audio = new Audio(audioUrl)
      audio.preload = 'auto'
      preloadedAudios.value[src] = audio
    })
  }

  onMounted(() => {
    preloadSounds()
  })

  // Watch sound enabled state and stop ambient sound when disabled
  watch(isSoundEnabled, enabled => {
    if (!enabled) {
      if (ambientAudio.value) {
        stopAmbientSound()
      }
      if (countdownAudio.value) {
        stopCountdown()
      }
    }
  })

  const play = (src: string, volume = 1, loop = false) => {
    if (!isSoundEnabled.value) return

    const audio = preloadedAudios.value[src]
    if (audio) {
      // Clone the audio to allow multiple simultaneous plays
      const audioClone = audio.cloneNode() as HTMLAudioElement
      audioClone.volume = volume
      audioClone.loop = loop
      audioClone.play().catch(e => {
        console.warn('Audio play blocked:', e)
      })
    } else {
      // Fallback to regular loading if preload failed
      const audioUrl = new URL(src, import.meta.url).href
      const fallbackAudio = new Audio(audioUrl)
      fallbackAudio.volume = volume
      fallbackAudio.loop = loop
      fallbackAudio.play().catch(e => {
        console.warn('Audio play blocked:', e)
      })
    }
  }

  const playAmbientSound = (volume = 0.3) => {
    if (!isSoundEnabled.value || ambientAudio.value) return

    const audioUrl = new URL('/audio/ambient.mp3', import.meta.url).href
    const audio = new Audio(audioUrl)
    audio.volume = volume
    audio.loop = true
    audio.play().catch(e => {
      console.warn('Ambient audio play blocked:', e)
    })
    ambientAudio.value = audio
  }

  const setAmbientVolume = (volume: number) => {
    if (ambientAudio.value) {
      ambientAudio.value.volume = volume
    }
  }

  const stopAmbientSound = () => {
    if (ambientAudio.value) {
      ambientAudio.value.pause()
      ambientAudio.value = null
    }
  }

  const playCountdown = () => {
    if (!isSoundEnabled.value) return

    stopCountdown()

    const audio = preloadedAudios.value['/audio/countdown.mp3']
    if (audio) {
      const audioClone = audio.cloneNode() as HTMLAudioElement
      audioClone.volume = 0.8
      audioClone.loop = true
      audioClone.play().catch(e => {
        console.warn('Countdown audio play blocked:', e)
      })
      countdownAudio.value = audioClone
    }
  }

  const stopCountdown = () => {
    if (countdownAudio.value) {
      countdownAudio.value.pause()
      countdownAudio.value = null
    }
  }

  return {
    playClick: () => play('/audio/button-click.mp3', 0.5),
    playCardTraded: () => play('/audio/card-traded.wav', 0.5),
    playAmbientSound,
    setAmbientVolume,
    stopAmbientSound,
    playGameWon: () => play('/audio/game-won.mp3', 0.7),
    playTrashCard: () => play('/audio/trash-card.mp3', 0.6),
    playCorrectAnswer: () => play('/audio/correct-answer.mp3', 0.5),
    playWrongAnswer: () => play('/audio/wrong-answer.mp3', 0.5),
    playCountdown,
    stopCountdown,
    playWordFormed: () => play('/audio/word-formed.mp3', 0.5)
  }
}
